import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import builderClient, { getBuilderToken, setBuilderToken } from "@/lib/builderApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, HardHat, Phone, ShieldCheck, LogOut, FileDown, MapPin, CheckCircle2, MessageSquarePlus, CalendarDays, Clock, Search, X, ChevronDown, Archive, PackageSearch, Inbox, Truck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LOGO = "/tilesmate-logo.png";

/* ----------------------------------- Login ----------------------------------- */
function Login() {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async () => {
    if (phone.trim().length < 8) { toast.error("Enter your mobile number"); return; }
    setBusy(true);
    try {
      await builderClient.post("/builder-portal/auth/request-otp", { phone: phone.trim() });
      toast.success("If your number is approved, a code is on its way.");
      setStep("code");
    } catch { toast.error("Something went wrong — please try again."); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (code.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await builderClient.post("/builder-portal/auth/verify-otp", { phone: phone.trim(), code: code.trim() });
      setBuilderToken(data.session_token);
      toast.success("Signed in");
      window.location.assign("/portal");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid or expired code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={LOGO} alt="TilesMate" className="mb-4 h-12 w-auto" />
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#D97706]/12 px-3 py-1 text-xs font-bold text-[#B45309]"><HardHat className="h-3.5 w-3.5" />Builder Portal</div>
          <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-slate-900">Your sites &amp; selections</h1>
          <p className="mt-1 text-sm text-slate-500">Secure sign-in with a code sent to your mobile.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
          {step === "phone" ? (
            <div className="space-y-4" data-testid="portal-phone-step">
              <div className="grid gap-1.5">
                <Label className="text-xs">Mobile number</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && requestCode()} placeholder="0412 345 678" inputMode="tel" autoFocus data-testid="portal-phone-input" />
              </div>
              <Button className="h-11 w-full gap-2 active:scale-[0.98]" onClick={requestCode} disabled={busy} data-testid="portal-request-otp-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}Send me a code</Button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5" />Only approved numbers can access this portal.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="portal-code-step">
              <div className="grid gap-1.5">
                <Label className="text-xs">6-digit code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="••••••" inputMode="numeric" autoFocus className="text-center text-xl tracking-[0.5em]" data-testid="portal-code-input" />
              </div>
              <Button className="h-11 w-full gap-2 active:scale-[0.98]" onClick={verify} disabled={busy} data-testid="portal-verify-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Verify &amp; sign in</Button>
              <button onClick={() => setStep("phone")} className="w-full text-center text-xs text-slate-500 hover:text-slate-900">Use a different number</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Helpers --------------------------------- */
const fmtTimeline = (v) => {
  if (!v) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  try { return new Date(v + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return v; }
};
function deliveryBadge(st) {
  if (st.delivery_status === "scheduled") return { label: `Scheduled · ${fmtTimeline(st.scheduled_date)}`, bg: "bg-[#ECFDF5]", text: "text-[#047857]", dot: "bg-[#10B981]" };
  if (st.delivery_status === "tentative") return { label: "Tentative — to be confirmed", bg: "bg-[#FFFBEB]", text: "text-[#B45309]", dot: "bg-[#F59E0B]" };
  if (st.delivery_status === "to_confirm") return { label: "Delivery — to be confirmed", bg: "bg-[#FFF7ED]", text: "text-[#C2410C]", dot: "bg-[#EA580C]" };
  return { label: fmtTimeline(st.delivery_timeline) || "Delivery TBC", bg: "bg-slate-100", text: "text-slate-500", dot: "bg-slate-400" };
}
const stagePill = (s) => s === "done"
  ? "border-[#A7F3D0] bg-[#D1FAE5] text-[#065F46]"
  : "border-[#BFDBFE] bg-[#EFF6FF] text-[#1E40AF]";

const siteNeedsAction = (site) => (site.stages || []).some((s) => s.delivery_status === "tentative" || s.delivery_status === "to_confirm");

/* ------------------------------ Change request ----------------------------- */
function ChangeDialog({ site, open, onOpenChange, onDone }) {
  const [type, setType] = useState("date");
  const [stage, setStage] = useState("");
  const [requestedDate, setRequestedDate] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await builderClient.post(`/builder-portal/sites/${site.id}/change-request`, { type, stage: stage || undefined, requested_date: requestedDate, message });
      toast.success("Request sent to the TilesMate team");
      onOpenChange(false); setRequestedDate(""); setMessage(""); setStage(""); setType("date");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-request-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Request a change — {site.title}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-1">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="cr-type-toggle">
            {[["date", "Delivery date"], ["item", "Item / qty"], ["note", "General note"]].map(([v, l]) => (
              <button key={v} onClick={() => setType(v)} data-testid={`cr-type-${v}`} className={cn("rounded-md px-3 py-1.5 text-xs font-semibold transition-colors", type === v ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-900")}>{l}</button>
            ))}
          </div>
          {site.stages?.length > 0 && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Which stage? (optional)</Label>
              <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="cr-stage-select" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm focus-visible:ring-2 focus-visible:ring-amber-500">
                <option value="">Whole site</option>
                {site.stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          )}
          {type === "date" && (
            <div className="grid gap-1.5"><Label className="text-xs">Preferred delivery date</Label>
              <Input value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} placeholder="e.g. Week of 12 May, or 2026-05-12" data-testid="cr-date-input" /></div>
          )}
          <div className="grid gap-1.5"><Label className="text-xs">{type === "date" ? "Anything else? (optional)" : "Details"}</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={type === "item" ? "e.g. Change 600x600 Matt from 40 to 55 boxes" : "Describe your request"} className="min-h-[70px]" data-testid="cr-message-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="cr-submit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Stage accordion ----------------------------- */
function StageAccordion({ site, st, onPdf }) {
  const [open, setOpen] = useState(false);
  const badge = deliveryBadge(st);
  const preview = st.items.slice(0, 2).map((i) => i.name).join(", ");
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`portal-stage-${site.id}-${st.key}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`portal-stage-toggle-${site.id}-${st.key}`}
        className="flex w-full items-center gap-2 px-3.5 py-3 text-left transition-colors hover:bg-slate-50">
        <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold", stagePill(st.status))}>{st.label}{st.status === "done" ? " ✓" : ""}</span>
        <span className={cn("inline-flex min-w-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold", badge.bg, badge.text)}>
          <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", badge.dot)} /><span className="truncate">{badge.label}</span>
        </span>
        <span className="ml-auto shrink-0 text-[11px] font-medium text-slate-400">{st.items.length} {st.items.length === 1 ? "item" : "items"}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200", open && "rotate-180")} />
      </button>
      {!open && st.items.length > 0 && (
        <p className="truncate px-3.5 pb-2.5 text-[11px] text-slate-400">{preview}{st.items.length > 2 ? ` +${st.items.length - 2} more` : ""}</p>
      )}
      {open && (
        <div className="border-t border-slate-100 px-3.5 py-3">
          {st.delivery_status === "scheduled" && st.delivery_timeline && (
            <p className="mb-2 text-[11px] text-slate-400">Original estimate: {fmtTimeline(st.delivery_timeline)}</p>
          )}
          {st.items.length > 0 ? (
            <ul className="space-y-1">
              {st.items.map((it, i) => (
                <li key={i} className="flex items-center justify-between gap-3 border-b border-slate-50 pb-1 text-xs last:border-0">
                  <span className="min-w-0 truncate text-slate-600">{it.name}{it.sku ? <span className="text-slate-400"> · {it.sku}</span> : ""}</span>
                  <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-700">×{it.qty}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs text-slate-400">No items listed for this stage yet.</p>}
          <button onClick={() => onPdf(st.key)} data-testid={`portal-stage-pdf-${site.id}-${st.key}`}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition-colors hover:border-amber-400 hover:text-amber-700 active:scale-[0.98]">
            <FileDown className="h-3.5 w-3.5" />Stage PDF
          </button>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Site card -------------------------------- */
function SiteCard({ site, onChanged }) {
  const [crOpen, setCrOpen] = useState(false);
  const pct = site.stage_count ? Math.round((site.done_count / site.stage_count) * 100) : 0;
  const dl = async (stage) => {
    try {
      const res = await builderClient.get(`/builder-portal/sites/${site.id}/pdf`, { params: stage ? { stage } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `TilesMate-${site.title}${stage ? "-" + stage : ""}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };
  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5" data-testid={`portal-site-${site.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-lg font-bold tracking-tight text-slate-900">{site.title}</h3>
          {site.address && <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500"><MapPin className="h-3 w-3 shrink-0" />{site.address}</p>}
        </div>
        <span className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold", site.archived ? "bg-[#D1FAE5] text-[#065F46]" : "bg-slate-100 text-slate-600")}>
          {site.archived ? "Completed" : `${site.done_count}/${site.stage_count} done`}
        </span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={cn("h-full rounded-full transition-[width] duration-500", site.archived ? "bg-[#10B981]" : "bg-[#D97706]")} style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-4 space-y-2">
        {site.stages.map((st) => <StageAccordion key={st.key} site={site} st={st} onPdf={dl} />)}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button size="sm" className="gap-1.5 active:scale-[0.98]" onClick={() => dl(null)} data-testid={`portal-site-pdf-${site.id}`}><FileDown className="h-4 w-4" />Full selections PDF</Button>
        <Button variant="outline" size="sm" className="gap-1.5 active:scale-[0.98]" onClick={() => setCrOpen(true)} data-testid={`request-change-${site.id}`}><MessageSquarePlus className="h-4 w-4" />Request a change</Button>
      </div>
      <ChangeDialog site={site} open={crOpen} onOpenChange={setCrOpen} onDone={onChanged} />
    </div>
  );
}

/* -------------------------------- Dashboard -------------------------------- */
const FILTERS = [
  ["active", "Active", "portal-filter-in-progress"],
  ["action", "Action needed", "portal-filter-action-needed"],
  ["deliveries", "Deliveries", "portal-filter-deliveries"],
  ["archived", "Archived", "portal-filter-completed"],
];

function fmtDate(d) {
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return d;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((dt - today) / 86400000);
  const txt = dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const rel = diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : diff > 1 && diff <= 7 ? `In ${diff} days` : diff < 0 ? "Overdue" : null;
  return rel ? `${txt} · ${rel}` : txt;
}

function DeliveryRow({ row, firm }) {
  const { site, st } = row;
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`portal-delivery-${site.id}-${st.key}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold", stagePill(st.status))}>{st.label}</span>
          <span className="truncate text-sm font-semibold text-slate-900">{site.title}</span>
        </div>
        {site.address && <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-500"><MapPin className="h-3 w-3 shrink-0" />{site.address}</div>}
      </div>
      {firm ? (
        <div className="shrink-0 text-right">
          <div className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[11px] font-bold text-[#047857]"><CalendarDays className="h-3 w-3" />{fmtDate(row.date)}</div>
          <div className="mt-0.5 text-[10px] text-slate-400">{st.items.length} {st.items.length === 1 ? "item" : "items"}</div>
        </div>
      ) : (
        <span className="shrink-0 rounded-full bg-[#FFF7ED] px-2 py-0.5 text-[11px] font-semibold text-[#C2410C]">Date TBC</span>
      )}
    </div>
  );
}

function DeliveriesView({ deliveries, q, onClear }) {
  if (deliveries.total === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 py-14 text-center" data-testid="portal-no-deliveries">
        <Truck className="mx-auto mb-3 h-8 w-8 text-slate-300" />
        <p className="text-sm font-semibold text-slate-700">No upcoming deliveries</p>
        <p className="mt-1 text-xs text-slate-500">{q ? `Nothing matches "${q}".` : "Deliveries will appear here once your rep schedules them."}</p>
        {q && <button onClick={onClear} className="mt-3 text-xs font-semibold text-amber-700 hover:underline">Clear search</button>}
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="portal-deliveries">
      {deliveries.firm.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-[#047857]"><CalendarDays className="h-3.5 w-3.5" />Scheduled</div>
          <div className="divide-y divide-slate-100">
            {deliveries.firm.map((r) => <DeliveryRow key={r.site.id + r.st.key} row={r} firm />)}
          </div>
        </div>
      )}
      {deliveries.tbc.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-[#C2410C]"><Clock className="h-3.5 w-3.5" />To be confirmed</div>
          <div className="divide-y divide-slate-100">
            {deliveries.tbc.map((r) => <DeliveryRow key={r.site.id + r.st.key} row={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ me }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("active");
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touch = useRef(null);
  const sites = useQuery({ queryKey: ["portal-sites"], queryFn: async () => (await builderClient.get("/builder-portal/sites")).data });
  const reqs = useQuery({ queryKey: ["portal-reqs"], queryFn: async () => (await builderClient.get("/builder-portal/change-requests")).data });
  const logout = async () => { try { await builderClient.post("/builder-portal/auth/logout"); } catch {} setBuilderToken(null); window.location.assign("/portal"); };

  const all = sites.data || [];
  const term = q.trim().toLowerCase();
  const counts = useMemo(() => {
    let deliveries = 0;
    for (const s of all) {
      if (s.archived) continue;
      for (const st of s.stages || []) {
        if (st.status === "done") continue;
        if ((st.delivery_status === "scheduled" && st.scheduled_date) || st.delivery_status === "tentative" || st.delivery_status === "to_confirm") deliveries++;
      }
    }
    return {
      active: all.filter((s) => !s.archived).length,
      action: all.filter((s) => !s.archived && siteNeedsAction(s)).length,
      deliveries,
      archived: all.filter((s) => s.archived).length,
    };
  }, [all]);

  const filtered = useMemo(() => {
    let base = all;
    if (filter === "active") base = all.filter((s) => !s.archived);
    else if (filter === "archived") base = all.filter((s) => s.archived);
    else if (filter === "action") base = all.filter((s) => !s.archived && siteNeedsAction(s));
    if (!term) return base;
    return base.filter((s) => {
      if ((s.title || "").toLowerCase().includes(term) || (s.address || "").toLowerCase().includes(term)) return true;
      return (s.stages || []).some((st) =>
        (st.label || "").toLowerCase().includes(term) ||
        (st.items || []).some((it) => (it.name || "").toLowerCase().includes(term) || (it.sku || "").toLowerCase().includes(term)));
    });
  }, [all, filter, term]);

  const deliveries = useMemo(() => {
    const rows = [];
    for (const s of all) {
      if (s.archived) continue;
      for (const st of s.stages || []) {
        if (st.status === "done") continue;
        const isFirm = st.delivery_status === "scheduled" && st.scheduled_date;
        const isTbc = st.delivery_status === "tentative" || st.delivery_status === "to_confirm";
        if (!isFirm && !isTbc) continue;
        if (term) {
          const hay = `${s.title || ""} ${s.address || ""} ${st.label || ""} ${(st.items || []).map((i) => (i.name || "") + " " + (i.sku || "")).join(" ")}`.toLowerCase();
          if (!hay.includes(term)) continue;
        }
        rows.push({ site: s, st, date: isFirm ? st.scheduled_date : null });
      }
    }
    const firm = rows.filter((r) => r.date).sort((a, b) => a.date.localeCompare(b.date));
    const tbc = rows.filter((r) => !r.date);
    return { firm, tbc, total: rows.length };
  }, [all, term]);

  const openReqCount = (reqs.data || []).filter((r) => r.status === "open").length;
  const scrollToReqs = () => document.getElementById("my-requests")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const refetchAll = async () => {
    setRefreshing(true);
    try { await Promise.all([sites.refetch(), reqs.refetch()]); }
    finally { setTimeout(() => setRefreshing(false), 400); }
  };

  const idx = FILTERS.findIndex((f) => f[0] === filter);
  const onTouchStart = (e) => {
    if (e.touches.length !== 1) { touch.current = null; return; }
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, axis: null, atTop: window.scrollY <= 0 };
  };
  const onTouchMove = (e) => {
    const t = touch.current; if (!t) return;
    const dx = e.touches[0].clientX - t.x;
    const dy = e.touches[0].clientY - t.y;
    if (!t.axis && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) t.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    if (t.axis === "y" && t.atTop && dy > 0 && !refreshing) setPull(Math.min(dy * 0.5, 80));
  };
  const onTouchEnd = (e) => {
    const t = touch.current; if (!t) { setPull(0); return; }
    const dx = e.changedTouches[0].clientX - t.x;
    if (t.axis === "x" && Math.abs(dx) > 60) {
      const next = Math.min(Math.max(idx + (dx < 0 ? 1 : -1), 0), FILTERS.length - 1);
      if (next !== idx) setFilter(FILTERS[next][0]);
    } else if (t.axis === "y" && pull > 55 && !refreshing) {
      refetchAll();
    }
    setPull(0);
    touch.current = null;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sticky header + search/filter */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6" data-testid="portal-header">
          <div className="flex min-w-0 items-center gap-3">
            <img src={LOGO} alt="TilesMate" className="h-8 w-auto" />
            <div className="min-w-0">
              <div className="truncate font-display text-sm font-bold text-slate-900">{me?.builder?.name}</div>
              <div className="text-[11px] text-slate-500">Builder Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refetchAll} data-testid="portal-refresh-btn" className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-amber-400 hover:text-amber-700" aria-label="Refresh">
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            </button>
            {openReqCount > 0 && (
              <button onClick={scrollToReqs} data-testid="portal-my-requests-btn" className="relative inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 px-3 text-xs font-semibold text-slate-700 transition-colors hover:border-amber-400">
                <Inbox className="h-4 w-4" /><span className="hidden sm:inline">Requests</span>
                <span className="grid h-4 min-w-4 place-items-center rounded-full bg-[#D97706] px-1 text-[10px] font-bold text-white">{openReqCount}</span>
              </button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={logout} data-testid="portal-logout-btn"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Sign out</span></Button>
          </div>
        </div>
        <div className="mx-auto max-w-5xl px-4 pb-3 sm:px-6">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search site, address, product or SKU…" className="h-11 pl-9 pr-9 focus-visible:ring-amber-500" data-testid="portal-search-input" />
            {q && <button onClick={() => setQ("")} data-testid="portal-search-clear" className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-900"><X className="h-4 w-4" /></button>}
          </div>
          <div className="mt-2.5 flex gap-2 overflow-x-auto pb-0.5">
            {FILTERS.map(([v, l, tid]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={tid}
                className={cn("inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-xs font-semibold transition-colors",
                  filter === v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}>
                {v === "archived" && <Archive className="h-3.5 w-3.5" />}{v === "deliveries" && <Truck className="h-3.5 w-3.5" />}{l}
                <span className={cn("grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-bold", filter === v ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500")}>{counts[v]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pull-to-refresh indicator */}
      <div className="flex items-center justify-center overflow-hidden" style={{ height: refreshing ? 44 : pull }}>
        <RefreshCw className={cn("h-5 w-5 text-[#D97706]", refreshing && "animate-spin")} style={{ transform: refreshing ? "none" : `rotate(${pull * 4}deg)`, opacity: refreshing ? 1 : Math.min(pull / 40, 1) }} />
      </div>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-testid="portal-main">
        {sites.isLoading ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {[0, 1].map((i) => <div key={i} className="h-56 animate-pulse rounded-2xl bg-white shadow-sm" />)}
          </div>
        ) : all.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 py-14 text-center" data-testid="portal-no-sites">
            <PackageSearch className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm font-semibold text-slate-700">No sites assigned yet</p>
            <p className="mt-1 text-xs text-slate-500">Your TilesMate rep will add your sites here.</p>
          </div>
        ) : filter === "deliveries" ? (
          <DeliveriesView deliveries={deliveries} q={q} onClear={() => setQ("")} />
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 py-14 text-center" data-testid="portal-empty-filter">
            <Search className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm font-semibold text-slate-700">{filter === "archived" ? "No completed sites yet" : "Nothing matches"}</p>
            <p className="mt-1 text-xs text-slate-500">{term ? `No sites match "${q}".` : "Try a different filter."}</p>
            {(term || filter !== "active") && <button onClick={() => { setQ(""); setFilter("active"); }} className="mt-3 text-xs font-semibold text-amber-700 hover:underline">Clear search &amp; filters</button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2 lg:gap-x-4" data-testid="portal-sites">
            {filtered.map((s) => <SiteCard key={s.id} site={s} onChanged={() => qc.invalidateQueries({ queryKey: ["portal-reqs"] })} />)}
          </div>
        )}

        {(reqs.data || []).length > 0 && (
          <section id="my-requests" className="mt-8 scroll-mt-40 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="portal-my-requests">
            <h2 className="mb-3 font-display text-base font-bold text-slate-900">My change requests</h2>
            <div className="divide-y divide-slate-100">
              {reqs.data.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div className="min-w-0"><span className="font-semibold text-slate-900">{r.job_title}</span><span className="text-slate-500"> — {r.type}{r.requested_date ? ` · ${r.requested_date}` : ""}{r.message ? ` · ${r.message}` : ""}</span></div>
                  <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", r.status === "actioned" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FFFBEB] text-[#B45309]")}>{r.status === "actioned" ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{r.status === "actioned" ? "Actioned" : "Open"}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

/* --------------------------------- Wrapper --------------------------------- */
export default function BuilderPortal() {
  const token = getBuilderToken();
  const timer = useRef(null);
  useEffect(() => {
    if (!token) return undefined;
    const reset = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { setBuilderToken(null); window.location.assign("/portal"); }, 30 * 60 * 1000); };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer.current); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, [token]);
  const { data: me, isLoading, isError } = useQuery({ queryKey: ["portal-me", token], queryFn: async () => (await builderClient.get("/builder-portal/me")).data, enabled: !!token, retry: false });
  if (token && isLoading) return <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC]"><Loader2 className="h-7 w-7 animate-spin text-[#D97706]" /></div>;
  if (!token || isError || !me) return <Login />;
  return <Dashboard me={me} />;
}
